import i18n from 'i18next';
import type { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

// English ships in the main bundle so the first paint never waits on a locale.
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enOnboarding from './locales/en/onboarding.json';
import enDashboard from './locales/en/dashboard.json';
import enFeedback from './locales/en/feedback.json';
import enBoosts from './locales/en/boosts.json';
import enRecognition from './locales/en/recognition.json';
import enLayout from './locales/en/layout.json';
import enTray from './locales/en/tray.json';
import enProfile from './locales/en/profile.json';
import enAssistant from './locales/en/assistant.json';

export const LOCALE_NAMESPACES = [
  'common',
  'auth',
  'onboarding',
  'dashboard',
  'feedback',
  'boosts',
  'recognition',
  'layout',
  'tray',
  'profile',
  'assistant',
] as const;

export type LocaleNamespace = (typeof LOCALE_NAMESPACES)[number];
export type LocaleBundle = Record<LocaleNamespace, Record<string, unknown>>;

// Every other locale is fetched on demand the first time `changeLanguage` asks
// for it, as one chunk per language, so the ~180 kB of non-English strings stay
// out of the startup bundle.
const LAZY_LOCALE_LOADERS: Record<string, () => Promise<{ default: LocaleBundle }>> = {
  th: () => import('./locales/th/locale-th'),
  ms: () => import('./locales/ms/locale-ms'),
  'pt-BR': () => import('./locales/pt-BR/locale-pt-BR'),
  'zh-HK': () => import('./locales/zh-HK/locale-zh-HK'),
  my: () => import('./locales/my/locale-my'),
};

export const SUPPORTED_LANGUAGES: readonly string[] = ['en', ...Object.keys(LAZY_LOCALE_LOADERS)];

const localeBundleCache = new Map<string, Promise<LocaleBundle>>();

function loadLocaleBundle(language: string): Promise<LocaleBundle> | null {
  const loader = LAZY_LOCALE_LOADERS[language];
  if (!loader) return null;
  let pending = localeBundleCache.get(language);
  if (!pending) {
    pending = loader().then((module) => module.default);
    // A failed fetch must not poison the cache; the next changeLanguage retries.
    pending.catch(() => localeBundleCache.delete(language));
    localeBundleCache.set(language, pending);
  }
  return pending;
}

const lazyLocaleBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language: string, namespace: string, callback: ReadCallback) {
    const pending = loadLocaleBundle(language);
    if (!pending) {
      // Unknown language (or an intermediate like `pt` for `pt-BR`): nothing to
      // load, and i18next falls back to English for every key.
      callback(null, {});
      return;
    }
    pending.then(
      (bundle) => callback(null, bundle[namespace as LocaleNamespace] ?? {}),
      (error) => callback(error instanceof Error ? error : new Error(String(error)), null),
    );
  },
};

i18n.use(lazyLocaleBackend).use(initReactI18next).init({
  resources: {
    en: {
      common: enCommon,
      auth: enAuth,
      onboarding: enOnboarding,
      dashboard: enDashboard,
      feedback: enFeedback,
      boosts: enBoosts,
      recognition: enRecognition,
      layout: enLayout,
      tray: enTray,
      profile: enProfile,
      assistant: enAssistant,
    },
  },
  partialBundledLanguages: true,
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: [...LOCALE_NAMESPACES],
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

// Sync <html lang> attribute with current language for font switching
function syncHtmlLang(lng: string) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
}
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
